import type { PropsWithChildren } from 'react';
import React from 'react';
import cx from 'clsx';

import ExpandIcon from '~/assets/icons/ic__system__move_top__outline.svg';

import styles from './Accordion.module.css';

type PanelProps = {
  label: string | React.ReactNode;
  summary?: React.ReactNode;
  summaryVariant?: string;
  activeTab?: number;
  index: number;
  disabled?: boolean;
  activateTab?: () => void;
  onClick?: () => void;
  onClickDisabled?: () => void;
  className?: string;
};

const AccordionPanel = ({
  children,
  label,
  summary,
  summaryVariant,
  activeTab,
  index,
  activateTab,
  disabled,
  onClickDisabled,
  onClick,
  className,
}: PropsWithChildren<PanelProps>) => {
  const [height, setHeight] = React.useState(0);
  const innerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (innerRef.current) {
      setHeight(innerRef.current.scrollHeight);
    }
  }, [children]);

  const isActive = activeTab === index;

  const innerStyle = React.useMemo(
    () => ({
      height: `${isActive ? height : 0}px`,
    }),
    [height, isActive],
  );

  return (
    <div className={cx([styles.panel, className])} data-expanded={isActive}>
      <button
        type="button"
        className={styles.label}
        onClick={() => {
          if (disabled) {
            onClickDisabled?.();
            return;
          }
          onClick?.();
          activateTab?.();
        }}
      >
        <p className={styles.labelText}>{label}</p>
        <div className={styles.right}>
          {summary && !isActive && (
            <span
              className={cx([
                styles.summary,
                summaryVariant && styles[summaryVariant],
              ])}
            >
              {summary}
            </span>
          )}
          <ExpandIcon className={styles.icon} />
        </div>
      </button>
      <div className={styles.inner} style={innerStyle} ref={innerRef}>
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};

const Accordion = ({ children }: PropsWithChildren) => {
  const [activeTab, setActiveTab] = React.useState(-1);

  const activateTab = (index: number) => {
    setActiveTab((prev) => (prev === index ? -1 : index));
  };

  return (
    <div className={styles.accordion}>
      {React.Children.map(children, (child) => {
        const item = child as
          | React.ReactElement<React.PropsWithChildren<PanelProps>>
          | null
          | undefined
          | false;

        if (!item) {
          return null;
        }

        return React.cloneElement(item, {
          ...item.props,
          activeTab,
          activateTab: () => {
            activateTab(item.props.index);
          },
        });
      })}
    </div>
  );
};

type AccordionType = typeof Accordion & {
  Panel: typeof AccordionPanel;
};

(Accordion as AccordionType).Panel = AccordionPanel;

export default Accordion as AccordionType;
